import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import handleErrorForThunk from "helpers/handleErrorForThunk";
import { parseServerError } from "utils/errors";

export function normalizeInvoiceReportExportPath(path) {
  if (path == null || typeof path !== "string") return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  let p = trimmed.replace(/^\/api\/\d+\/?/i, "/");
  if (!p.startsWith("/")) {
    p = `/${p}`;
  }
  return p;
}

function normalizeReportItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  const report_id = raw.report_id ?? raw.reportId ?? raw.id;
  if (report_id == null) return null;
  const normalized = {
    report_id,
    title: raw.title ?? "",
    description: raw.description ?? "",
    query_params: raw.query_params ?? raw.queryParams ?? null,
    formats: raw.formats ?? raw.file_type ?? null,
    export_path: raw.export_path ?? raw.exportPath ?? null,
    format_query_param:
      raw.format_query_param ??
      raw.formatQueryParam ??
      raw.file_type_param ??
      null,
  };
  for (const [key, val] of Object.entries(raw)) {
    if (Array.isArray(val)) {
      normalized[key] = val;
    }
  }
  return normalized;
}

function normalizeReportsPayload(payload) {
  let list = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (payload && typeof payload === "object") {
    list =
      payload.items ||
      payload.data ||
      payload.reports ||
      payload.results ||
      [];
  }
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map(normalizeReportItem).filter(Boolean);
}

function normalizeExportItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id ?? raw.export_id ?? raw.exportId;
  if (id == null) return null;
  return {
    id,
    from_date: raw.from_date ?? raw.fromDate ?? "",
    till_date: raw.till_date ?? raw.tillDate ?? "",
    direction: raw.direction ?? raw.report_direction ?? raw.reportDirection ?? "",
    file_type: raw.file_type ?? raw.fileType ?? "",
    status: raw.status != null ? String(raw.status) : "",
    error_message: raw.error_message ?? raw.errorMessage ?? "",
    download_url: raw.download_url ?? raw.downloadUrl ?? "",
    created_at: raw.created_at ?? raw.createdAt ?? "",
    updated_at: raw.updated_at ?? raw.updatedAt ?? "",
    report_display:
      raw.report_display ?? raw.report_label ?? raw.report_summary ?? "",
    report_type: raw.report_type ?? raw.reportType ?? "",
    country: raw.country ?? raw.country_code ?? raw.country_name ?? "",
    added_by:
      raw.added_by ??
      raw.added_by_name ??
      raw.created_by ??
      raw.created_by_name ??
      raw.user_email ??
      raw.user_name ??
      "",
  };
}

function normalizeExportsPayload(payload) {
  let list = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (payload && typeof payload === "object") {
    list =
      payload.items ||
      payload.data ||
      payload.exports ||
      payload.results ||
      [];
  }
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map(normalizeExportItem).filter(Boolean);
}

const DOC_REPORTS_MODELS_NAME = "doc-reports";

export const fetchInvoiceReportsMessages = createAsyncThunk(
  "invoiceReports/fetchInvoiceReportsMessages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/notify/get_system_message_active?models_name=${encodeURIComponent(
          DOC_REPORTS_MODELS_NAME
        )}`,
        "GET"
      );
      if (response.ok) {
        return await response.json();
      }
      return rejectWithValue(response.status);
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchInvoiceReports = createAsyncThunk(
  "invoiceReports/fetchInvoiceReports",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents/reports", "GET");
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        let message = text || `HTTP ${response.status}`;
        try {
          const errJson = JSON.parse(text);
          message =
            errJson.error ||
            errJson.detail ||
            errJson.message ||
            message;
        } catch {
        }
        return rejectWithValue(message);
      }
      const data = await response.json();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || "Server error");
    }
  }
);

export const fetchInvoiceReportExports = createAsyncThunk(
  "invoiceReports/fetchInvoiceReportExports",
  async (_arg, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents/reports/exports", "GET");
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        let message = text || `HTTP ${response.status}`;
        try {
          const errJson = JSON.parse(text);
          message =
            errJson.error ||
            errJson.detail ||
            errJson.message ||
            message;
        } catch {
        }
        return rejectWithValue(message);
      }
      const data = await response.json();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || "Server error");
    }
  }
);

export const deleteInvoiceReportExport = createAsyncThunk(
  "invoiceReports/deleteInvoiceReportExport",
  async (exportId, { rejectWithValue }) => {
    try {
      if (exportId == null || exportId === "") {
        return rejectWithValue({ error: "Invalid export id" });
      }
      const id = encodeURIComponent(String(exportId));
      const response = await callApi(`/documents/reports/exports/${id}`, "DELETE");
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        let message = text || `HTTP ${response.status}`;
        try {
          const errJson = JSON.parse(text);
          message =
            errJson.error ||
            errJson.detail ||
            errJson.message ||
            message;
        } catch {
        }
        return rejectWithValue(message);
      }
      return exportId;
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

export const createInvoiceReport = createAsyncThunk(
  "invoiceReports/createInvoiceReport",
  async ({ exportPath, queryParams }, { rejectWithValue }) => {
    try {
      const path = normalizeInvoiceReportExportPath(exportPath);
      if (!path) {
        return rejectWithValue({ error: "Invalid report export path" });
      }
      const response = await callApi(path, "GET", queryParams || {});
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        let message = text || `HTTP ${response.status}`;
        try {
          const errJson = JSON.parse(text);
          message =
            errJson.error ||
            errJson.detail ||
            errJson.message ||
            message;
        } catch {
        }
        return rejectWithValue(message);
      }
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        try {
          return await response.json();
        } catch {
          return null;
        }
      }
      return null;
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  exportsItems: [],
  exportsLoading: false,
  exportsError: null,
  creatingReport: false,
  createReportError: null,
  createReportSuccess: false,
  deleteExportLoading: false,
  deleteExportError: null,
  messages: [],
  fetchInvoiceReportsMessages: false,
  fetchInvoiceReportsMessagesError: null,
};

const invoiceReportsSlice = createSlice({
  name: "invoiceReports",
  initialState,
  reducers: {
    clearInvoiceReportsError(state) {
      state.error = null;
    },
    clearInvoiceReportCreateFeedback(state) {
      state.createReportError = null;
      state.createReportSuccess = false;
    },
    clearInvoiceReportExportsError(state) {
      state.exportsError = null;
    },
    clearDeleteExportError(state) {
      state.deleteExportError = null;
    },
    clearInvoiceReportsMessages(state) {
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoiceReportsMessages.pending, (state) => {
        state.fetchInvoiceReportsMessages = true;
        state.fetchInvoiceReportsMessagesError = null;
      })
      .addCase(fetchInvoiceReportsMessages.fulfilled, (state, action) => {
        state.fetchInvoiceReportsMessages = false;
        state.fetchInvoiceReportsMessagesError = null;
        state.messages = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchInvoiceReportsMessages.rejected, (state, action) => {
        state.fetchInvoiceReportsMessages = false;
        state.fetchInvoiceReportsMessagesError = action.payload;
      })
      .addCase(fetchInvoiceReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceReports.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = normalizeReportsPayload(action.payload);
      })
      .addCase(fetchInvoiceReports.rejected, (state, action) => {
        state.loading = false;
        state.items = [];
        const fromPayload = action.payload;
        state.error =
          fromPayload != null
            ? parseServerError(fromPayload) || String(fromPayload)
            : parseServerError(action.error);
      })
      .addCase(createInvoiceReport.pending, (state) => {
        state.creatingReport = true;
        state.createReportError = null;
        state.createReportSuccess = false;
      })
      .addCase(createInvoiceReport.fulfilled, (state) => {
        state.creatingReport = false;
        state.createReportError = null;
        state.createReportSuccess = true;
      })
      .addCase(createInvoiceReport.rejected, (state, action) => {
        state.creatingReport = false;
        state.createReportSuccess = false;
        const fromPayload = action.payload;
        state.createReportError =
          fromPayload != null
            ? parseServerError(fromPayload) || String(fromPayload)
            : parseServerError(action.error);
      })
      .addCase(fetchInvoiceReportExports.pending, (state, action) => {
        const silent = Boolean(action.meta?.arg?.silent);
        if (!silent) {
          state.exportsLoading = true;
          state.exportsError = null;
        }
      })
      .addCase(fetchInvoiceReportExports.fulfilled, (state, action) => {
        state.exportsLoading = false;
        state.exportsError = null;
        state.exportsItems = normalizeExportsPayload(action.payload);
      })
      .addCase(fetchInvoiceReportExports.rejected, (state, action) => {
        const silent = Boolean(action.meta?.arg?.silent);
        state.exportsLoading = false;
        if (!silent) {
          state.exportsItems = [];
        }
        const fromPayload = action.payload;
        state.exportsError =
          fromPayload != null
            ? parseServerError(fromPayload) || String(fromPayload)
            : parseServerError(action.error);
      })
      .addCase(deleteInvoiceReportExport.pending, (state) => {
        state.deleteExportLoading = true;
        state.deleteExportError = null;
      })
      .addCase(deleteInvoiceReportExport.fulfilled, (state) => {
        state.deleteExportLoading = false;
        state.deleteExportError = null;
      })
      .addCase(deleteInvoiceReportExport.rejected, (state, action) => {
        state.deleteExportLoading = false;
        const fromPayload = action.payload;
        state.deleteExportError =
          fromPayload != null
            ? parseServerError(fromPayload) || String(fromPayload)
            : parseServerError(action.error);
      });
  },
});

export const {
  clearInvoiceReportsError,
  clearInvoiceReportCreateFeedback,
  clearInvoiceReportExportsError,
  clearDeleteExportError,
  clearInvoiceReportsMessages,
} = invoiceReportsSlice.actions;

export { DOC_REPORTS_MODELS_NAME };
export default invoiceReportsSlice.reducer;
